/**
 * LeadtimeReliabilityPanel — supplier dossier Performance-tab panel
 * (Lane 156, Track B / B4).
 *
 * Displayed vs actual order-to-receipt lead time for one supplier's measured
 * materials, fed by GET /api/suppliers/{id}/leadtime — a read-only surface
 * over the Lane-148 lead-time engine (same SQL grain, same tenant order-date
 * gate as the FAMILY_LEADTIME findings and the Lane-153 deck slide), so this
 * panel can never disagree with a finding.
 *
 * Double OFF-lock (the Lane-51 OpportunityCallout pattern): reads
 * `leadtimeUiEnabled` from AuthContext and bails (no fetch, renders null)
 * when the env flag is off; the backend endpoint independently gates on the
 * SAME env var. Fetch errors render null — the Performance tab must never
 * break on this panel.
 *
 * PLACEMENT NOTE (consult #11 condition i — a documented conscious decision):
 * this panel mounts inside SupplierDetail's Performance tab, which only
 * exists when the per-company `scorecard_ui_enabled` DB flag is on — so the
 * supplier-side lead-time surface is TRANSITIVELY gated on the scorecard UI
 * flag in addition to OPPORTUNITIES_LEADTIME_ENABLED. Accepted: the
 * execution-plan B4 spec puts the panel "beside on-time %" (the Performance
 * tab), and every live tenant candidate runs scorecard UI on. If a tenant
 * ever needs lead-time WITHOUT scorecards, move the mount, not the gate.
 *
 * States:
 *   - flag OFF / no supplierId (lazy resolution not landed) -> null
 *   - tenant has no real order dates -> honest note, never a fabricated
 *     lead time (XMEK today: po_date IS the receipt date — Lane 146)
 *   - order dates but nothing measured for this supplier -> one quiet line
 *   - data -> summary line + significantly-padded dollar line (the
 *     finding-parity sum; "significantly padded" wording per consult #11
 *     condition ii — the direction counts are band-only, the dollar covers
 *     only the significant-gap + floor-gated subset) + compact table.
 *
 * Lane 215 (Linear rebuild, Stage 2 — dossier child chrome): the panel's
 * deep chrome reads the `.sd` Linear token layer (panel, border, hair, the
 * text tiers, warn) so it flips with the shell in the future dark lane; the
 * Lane-148 chips render as .sd-badge tones. Presentation only — data, fetch,
 * states, and testids are unchanged.
 */
import React, { useEffect, useState } from 'react';
import { useAuth } from '../AuthContext';
import { API_BASE } from '../config';

// Linear panel chrome (Lane 198 `.sd-panel` equivalent), applied inline so
// each child keeps its exact spacing while reading the shared `.sd` tokens.
const LINEAR_PANEL = {
  background: 'var(--panel)',
  border: '1px solid var(--border)',
  borderRadius: '12px',
  boxShadow: 'var(--shadow)',
};

// Lane-148 direction chip -> Linear badge tone (ALIGNED good / PADDED warn /
// UNDERSTATED crit). Chip TEXT is unchanged.
const CHIP_BADGE_CLASS = { ALIGNED: 'good', PADDED: 'warn', UNDERSTATED: 'crit' };

const fmtUsd = (v) =>
  new Intl.NumberFormat('en-US', {
    style: 'currency', currency: 'USD',
    minimumFractionDigits: 0, maximumFractionDigits: 0,
  }).format(v);

const fmtDays = (v) => (v === null || v === undefined ? 'Not available' : `~${Math.round(Number(v))}d`);

const headerCell = {
  fontSize: 'var(--font-size-xs)',
  fontWeight: 'var(--font-weight-semibold)',
  color: 'var(--text-3)',
  textTransform: 'uppercase',
  letterSpacing: 'var(--letter-spacing-wide)',
  textAlign: 'left',
  padding: '6px 8px',
  borderBottom: '1px solid var(--border)',
};

const bodyCell = {
  fontSize: 'var(--font-size-sm)',
  color: 'var(--text)',
  padding: '6px 8px',
  borderBottom: '1px solid var(--hair)',
};

function LeadtimeReliabilityPanel({ supplierId, token }) {
  const { leadtimeUiEnabled } = useAuth();
  const [data, setData] = useState(null);

  const skip = !leadtimeUiEnabled || !supplierId || !token;

  useEffect(() => {
    if (skip) return undefined;
    let cancelled = false;
    fetch(`${API_BASE}/api/suppliers/${encodeURIComponent(supplierId)}/leadtime`, {
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
    })
      .then((res) => (res.ok ? res.json() : null))
      .then((body) => {
        if (!cancelled) setData(body);
      })
      .catch(() => {
        if (!cancelled) setData(null);
      });
    return () => { cancelled = true; };
  }, [supplierId, token, leadtimeUiEnabled, skip]);

  // Double OFF-lock + lazy-resolution no-op + quiet failure: the tab never
  // breaks (or changes at all) because of this panel.
  if (skip || !data || data.leadtime_enabled === false) return null;

  const windowMonths = data.window_months?.actual_lt;
  const windowLabel = windowMonths ? `last ${windowMonths} months of receipts` : 'recent receipts';

  let body;
  if (data.has_order_dates === false) {
    // The honest XMEK state: no real order date in the feed — say so, never
    // fabricate a lead time.
    body = (
      <p data-testid="leadtime-panel-no-order-dates" style={{
        fontSize: 'var(--font-size-sm)', color: 'var(--text-3)', margin: 0,
      }}>
        No real order-to-receipt data yet. Your PO history doesn&apos;t carry a
        usable order date. Actual lead times will appear here once it does.
      </p>
    );
  } else if (!data.has_data) {
    body = (
      <p data-testid="leadtime-panel-empty" style={{
        fontSize: 'var(--font-size-sm)', color: 'var(--text-3)', margin: 0,
      }}>
        No received orders with a real order date for this supplier in the {windowLabel}.
      </p>
    );
  } else {
    const s = data.summary || {};
    const segments = [
      `${s.measured_pairs} material${s.measured_pairs !== 1 ? 's' : ''} measured`,
      s.aligned > 0 ? `${s.aligned} aligned` : null,
      s.padded > 0 ? `${s.padded} padded` : null,
      s.understated > 0 ? `${s.understated} understated` : null,
    ].filter(Boolean);
    const rows = data.rows || [];
    const moreCount = (data.total_measured_pairs || rows.length) - rows.length;

    body = (
      <>
        <div data-testid="leadtime-panel-summary" style={{
          fontSize: 'var(--font-size-sm)', color: 'var(--text-2)', marginBottom: '8px',
        }}>
          {segments.join(' · ')}
        </div>
        {s.padded_dollars > 0 && (
          <div data-testid="leadtime-panel-padded-dollars" style={{
            fontSize: 'var(--font-size-sm)', color: 'var(--warn)',
            fontWeight: 'var(--font-weight-semibold)', marginBottom: '10px',
          }}>
            Ordering to real lead times on {s.padded_significant_pairs} significantly
            padded part{s.padded_significant_pairs !== 1 ? 's' : ''} could free
            about {fmtUsd(s.padded_dollars)} tied up in early-ordered stock.
          </div>
        )}
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              <th style={headerCell}>Material</th>
              <th style={{ ...headerCell, textAlign: 'right' }}>Shown</th>
              <th style={{ ...headerCell, textAlign: 'right' }}>Actual</th>
              <th style={{ ...headerCell, textAlign: 'right' }}>Receipts</th>
              <th style={{ ...headerCell, textAlign: 'center' }}>Read</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={`${r.material_number}`} title={r.reason || ''}>
                <td style={bodyCell}>
                  <span style={{ fontWeight: 'var(--font-weight-medium)' }}>{r.material_number}</span>
                  {r.material_name && (
                    <span style={{ color: 'var(--text-3)', fontSize: 'var(--font-size-xs)', marginLeft: '6px' }}>
                      {r.material_name}
                    </span>
                  )}
                </td>
                <td style={{ ...bodyCell, textAlign: 'right' }}>{fmtDays(r.displayed_lt_days)}</td>
                <td style={{ ...bodyCell, textAlign: 'right' }}>{fmtDays(r.actual_avg_lt_days)}</td>
                <td style={{ ...bodyCell, textAlign: 'right', color: 'var(--text-2)' }}>{r.po_count}</td>
                <td style={{ ...bodyCell, textAlign: 'center' }}>
                  {r.chip ? (
                    <span className={`sd-badge ${CHIP_BADGE_CLASS[r.chip] || 'mut'}`}>{r.chip}</span>
                  ) : (
                    <span style={{ color: 'var(--text-3)' }}>Not available</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {moreCount > 0 && (
          <div style={{
            fontSize: 'var(--font-size-xs)', color: 'var(--text-3)', marginTop: '6px',
          }}>
            and {moreCount} more measured material{moreCount !== 1 ? 's' : ''}
          </div>
        )}
      </>
    );
  }

  return (
    <div style={{ ...LINEAR_PANEL, padding: '16px', marginTop: '16px' }}>
      <div data-testid="leadtime-reliability-panel">
        <div style={{
          fontSize: 'var(--font-size-xs)', fontWeight: 'var(--font-weight-semibold)',
          color: 'var(--text-3)', textTransform: 'uppercase',
          letterSpacing: 'var(--letter-spacing-wide)', marginBottom: '4px',
        }}>
          Lead-time reliability
        </div>
        <div style={{
          fontSize: 'var(--font-size-xs)', color: 'var(--text-3)', marginBottom: '10px',
        }}>
          Actual order-to-receipt lead time vs the number your system shows
          {windowMonths ? `, ${windowLabel}` : ''}.
        </div>
        {body}
      </div>
    </div>
  );
}

export default LeadtimeReliabilityPanel;
